import { useEffect, useRef, useState } from 'react'
import { api, ApiError } from '../api.ts'

interface MachineHit {
  id: string
  model: string
  manufacturerName: string
  catalogCode: string
}
interface OrderLine {
  id: string
  orderNumber: string
  customerName: string
}

/**
 * Intake — a machine physically arrives on the lot (SPEC 5.4). Pick the catalogue
 * machine, type the serial and year. If it fulfils an open order, link it here so
 * it's marked sold the moment it lands — closing the "sold in transit, received as
 * free" window. Purchase cost is entered separately by the owner (it's cost data).
 */
export function IntakeDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [q, setQ] = useState('')
  const [hits, setHits] = useState<MachineHit[]>([])
  const [machine, setMachine] = useState<MachineHit | null>(null)
  const [serial, setSerial] = useState('')
  const [year, setYear] = useState('')
  const [lines, setLines] = useState<OrderLine[]>([])
  const [lineId, setLineId] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  useEffect(() => {
    if (machine || q.trim().length < 2) {
      setHits([])
      return
    }
    const t = setTimeout(() => {
      api
        .get<{ machines: MachineHit[] }>(`/api/machines?search=${encodeURIComponent(q.trim())}`)
        .then((r) => setHits(r.machines.slice(0, 6)))
        .catch(() => setHits([]))
    }, 130)
    return () => clearTimeout(t)
  }, [q, machine])

  // When a machine is chosen, load any open order lines waiting for it.
  useEffect(() => {
    if (!machine) return
    api
      .get<{ lines: OrderLine[] }>(`/api/machines/${machine.id}/open-order-lines`)
      .then((r) => setLines(r.lines))
      .catch(() => setLines([]))
  }, [machine])

  async function submit() {
    if (!machine) return
    setBusy(true)
    setError(null)
    try {
      const r = await api.post<{ unit: { id: string } }>('/api/units', {
        machineId: machine.id,
        serialNumber: serial.trim() || undefined,
        productionYear: year ? Number(year) : undefined,
        customerOrderItemId: lineId || undefined,
      })
      onDone()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Nie udało się przyjąć egzemplarza')
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/30 flex items-start justify-center pt-24" onClick={onClose}>
      <div className="w-full max-w-lg bg-white rounded-lg shadow-xl border border-slate-200 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 pt-4 pb-2">
          <h2 className="font-bold text-slate-800">Przyjęcie egzemplarza na plac</h2>
          <p className="text-xs text-slate-400">Wybierz maszynę z katalogu, wpisz numer seryjny i rok produkcji.</p>
        </div>

        {!machine ? (
          <div className="px-4 pb-4">
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Szukaj maszyny: model, kod katalogowy…"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:border-brand-600"
            />
            {hits.length > 0 && (
              <div className="mt-1 border border-slate-200 rounded-md divide-y divide-slate-100">
                {hits.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => { setMachine(m); setQ(m.model) }}
                    className="w-full text-left px-3 py-2 hover:bg-brand-50 text-sm"
                  >
                    <span className="text-slate-800">{m.model}</span>
                    <span className="block text-xs text-slate-400">
                      {m.manufacturerName} · {m.catalogCode}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="px-4 pb-4 space-y-3">
            <div className="flex items-center justify-between bg-slate-50 rounded-md px-3 py-2">
              <div className="text-sm">
                <span className="text-slate-800">{machine.model}</span>
                <span className="block text-xs text-slate-400">{machine.manufacturerName} · {machine.catalogCode}</span>
              </div>
              <button onClick={() => { setMachine(null); setLines([]); setLineId('') }} className="text-xs text-slate-400 hover:text-slate-700">
                zmień
              </button>
            </div>

            <div className="flex gap-2">
              <label className="flex-1 text-xs text-slate-500">
                Numer seryjny
                <input
                  value={serial}
                  onChange={(e) => setSerial(e.target.value)}
                  placeholder="np. WAN-2025-0042"
                  className="mt-0.5 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm font-mono focus:border-brand-600"
                />
              </label>
              <label className="w-28 text-xs text-slate-500">
                Rok produkcji
                <input
                  type="number"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  placeholder="2025"
                  className="mt-0.5 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm focus:border-brand-600"
                />
              </label>
            </div>

            {lines.length > 0 && (
              <label className="block text-xs text-slate-500">
                Realizuje zamówienie (opcjonalnie)
                <select
                  value={lineId}
                  onChange={(e) => setLineId(e.target.value)}
                  className="mt-0.5 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm bg-white"
                >
                  <option value="">— egzemplarz na magazyn —</option>
                  {lines.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.orderNumber} · {l.customerName}
                    </option>
                  ))}
                </select>
                <span className="text-[11px] text-slate-400">
                  Dowiązanie oznacza egzemplarz jako sprzedany temu klientowi — zniknie z puli wolnych.
                </span>
              </label>
            )}

            {error && <div className="text-sm text-red-700">{error}</div>}

            <div className="flex justify-end gap-2 pt-1">
              <button onClick={onClose} className="text-sm text-slate-500 px-3 py-2">
                Anuluj
              </button>
              <button
                onClick={() => void submit()}
                disabled={busy}
                className="text-sm bg-brand-600 text-white px-4 py-2 rounded-md hover:bg-brand-700 disabled:opacity-50 font-medium"
              >
                {busy ? 'Przyjmuję…' : 'Przyjmij na plac'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
